/**
 * 마스터 데이터 — 화면이 고르는 목록(Application · Opportunity 상태 · 업종)의 정본(설정 > 관리자 > 마스터 데이터).
 *
 * 정본은 DB crm_masters(/api/sales/masters). 화면은 이 파일을 거쳐서만 읽는다:
 *   · applicationTypes.ts · dealStatus.ts · industryTypes.ts 가 여기 값을 먼저 보고, 없으면 코드의 기본값을 쓴다
 *     (마이그레이션 전 · 오프라인 · 첫 렌더에도 화면이 같은 값으로 뜬다 — 기본값 = 시드값).
 *   · 첫 렌더부터 맞게 보이도록 마지막으로 받은 값을 브라우저에 남겨 두고(localStorage) 바로 쓴다.
 *     서버 값은 뒤에서 다시 받아 바뀐 것만 반영한다(useMasters 를 부른 화면이 다시 그려진다).
 */
import { useEffect, useState } from "react";
import { apiFetch } from "../../lib/apiFetch";

export type MasterType = "application" | "deal_status" | "industry" | "application_group" | "industry_group" | "people_format";
/** 대분류가 있는 목록 → 그 대분류 종류 */
export const GROUP_TYPE_OF: Partial<Record<MasterType, MasterType>> = { application: "application_group", industry: "industry_group" };
export type MasterTone = "accent" | "success" | "danger" | "warning" | "muted";

export interface MasterRow {
  master_type: MasterType;
  code: string;
  name: string;
  sort_order: number;
  is_active: boolean;
  extra_json: {
    name_en?: string;
    aliases?: string[];
    hint?: string;
    hint_en?: string;
    tone?: MasterTone;
    /** 대분류 코드(application_group · industry_group 의 code). 비면 미분류 */
    group?: string;
    /** 대분류(application_group · industry_group) 아이콘 — "company" = 회사 로고(자사 표시) */
    icon?: "company" | "";
    /** people_format(code 'default') — 사람 목록 표시 형식(peopleFormat.ts) */
    format?: unknown;
  } | null;
  updated_at?: string;
}

type Masters = Partial<Record<MasterType, MasterRow[]>>;

const CACHE_KEY = "vault.crmMasters.v1";
const ENDPOINT = "/api/sales/masters?types=application,deal_status,industry,application_group,industry_group,people_format";

function readCache(): Masters {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? (parsed as Masters) : {};
  } catch {
    return {};
  }
}

let current: Masters = typeof window === "undefined" ? {} : readCache();
let version = 0;
let inflight: Promise<void> | null = null;
let loadedOnce = false;
const listeners = new Set<() => void>();

/** 그 종류의 마스터 행(끈 것 포함, 순서대로). 아직 없으면(마이그레이션 전 · 첫 로드 전) null → 부르는 쪽이 기본값 */
export function masterRows(type: MasterType): MasterRow[] | null {
  const rows = current[type];
  return rows && rows.length ? rows : null;
}

/** 서버에서 다시 받는다 — 마스터 화면이 고친 뒤 부른다(force) */
export function loadMasters(force = false): Promise<void> {
  if (inflight) return inflight;
  if (loadedOnce && !force) return Promise.resolve();
  inflight = (async () => {
    try {
      const res = await fetch(ENDPOINT);
      if (!res.ok) return;
      const json = (await res.json().catch(() => ({}))) as { masters?: Masters; storage?: string };
      if (json.storage === "unavailable") return; // 마이그레이션 전 — 코드 기본값 그대로
      const next = json.masters || {};
      const changed = JSON.stringify(next) !== JSON.stringify(current);
      current = next;
      try { window.localStorage.setItem(CACHE_KEY, JSON.stringify(next)); } catch { /* 저장 막힘 — 다음에 다시 받는다 */ }
      if (changed) { version += 1; listeners.forEach((fn) => fn()); }
    } catch { /* 네트워크 — 캐시·기본값 그대로 */ } finally {
      loadedOnce = true;
      inflight = null;
    }
  })();
  return inflight;
}

/**
 * 마스터 값이 바뀌면 다시 그린다 — 목록·상세·현황 화면 최상단(CRMLayout 등)에서 한 번 부르면
 * 그 아래 라벨·색·옵션이 새 값으로 다시 계산된다. 반환값은 바뀔 때마다 늘어나는 번호(memo 의존값으로 쓴다).
 */
export function useMasters(): number {
  const [v, setV] = useState(version);
  useEffect(() => {
    const fn = () => setV(version);
    listeners.add(fn);
    void loadMasters();
    if (v !== version) setV(version);
    return () => { listeners.delete(fn); };
  }, []); // eslint-disable-line
  return v;
}

/** 테스트용 — 캐시를 비우고 값을 직접 넣는다 */
export function __setMastersForTest(next: Masters): void {
  current = next;
  version += 1;
  listeners.forEach((fn) => fn());
}

/* ── 마스터 데이터 화면(설정 > 관리자 > 마스터 데이터)이 쓰는 API ───────────────────── */

const API = "/api/sales/masters";

/** 끈 것 포함 전체 — 화면은 이걸로 그린다. storage "local" = 마이그레이션 전(행이 없다) */
export async function fetchMastersForAdmin(): Promise<{ masters: Masters; empty: boolean; emptyTypes: MasterType[] }> {
  const json = await apiFetch<{ masters?: Masters; storage?: string }>(`${API}?types=application,deal_status,industry,application_group,industry_group,people_format`);
  const masters = json.masters || {};
  // 종류별로 — 시드는 종류마다 따로 적용될 수 있다(20261026d · 20261026e)
  const emptyTypes = (["application", "deal_status", "industry"] as MasterType[]).filter((k) => json.storage === "unavailable" || !masters[k]?.length);
  return { masters, empty: emptyTypes.length === 3, emptyTypes };
}

export async function createMaster(type: MasterType, row: { code: string; name: string; extra_json?: MasterRow["extra_json"] }): Promise<void> {
  await apiFetch(API, { method: "POST", json: { type, ...row } });
}

export async function updateMaster(type: MasterType, code: string, patch: { name?: string; is_active?: boolean; extra_json?: MasterRow["extra_json"] }): Promise<void> {
  await apiFetch(API, { method: "PUT", json: { type, code, ...patch } });
}

export async function reorderMasters(type: MasterType, codes: string[]): Promise<void> {
  await apiFetch(API, { method: "PUT", json: { op: "reorder", type, codes } });
}

export async function deleteMaster(type: MasterType, code: string): Promise<void> {
  await apiFetch(`${API}?type=${encodeURIComponent(type)}&code=${encodeURIComponent(code)}`, { method: "DELETE" });
}
